/*
 * Card gallery dots + slideshow reset.
 *
 * This used to be an inline <script> inside snippets/card-gallery.liquid, which meant the
 * whole body was emitted once per product card - 22 copies (~117 KB) on the homepage, more
 * on collection pages - and every copy ran a settle pass while the HTML was still parsing.
 * A pass reads scroller.clientWidth/scrollWidth and writes scrollLeft, so each one forces
 * layout; the homepage's galleries measured ~48 ms per pass, repeated at raf/120 ms/700 ms.
 *
 * Same public API and same end state, loaded once. The settle schedule is now per gallery:
 * a gallery settles when it comes within 300px of the viewport, so cards the visitor can
 * actually see behave exactly as before. Anything IntersectionObserver has not reached by
 * the load event is settled during idle time in small batches - IntersectionObserver is
 * inert in some embedded/hidden webviews, and the reset must not silently stop happening
 * there.
 */
(function () {
  var raf =
    typeof window.requestAnimationFrame === 'function'
      ? window.requestAnimationFrame.bind(window)
      : function (callback) {
          return window.setTimeout(callback, 16);
        };

  var idle =
    typeof window.requestIdleCallback === 'function'
      ? window.requestIdleCallback.bind(window)
      : function (callback) {
          return window.setTimeout(callback, 1);
        };

  var dots = window.JnorssCardDots;
  if (!dots) {
    dots = {
      setActive: function (target, index) {
        var wrap = target.querySelector('.card-gallery__dots');
        if (!wrap) return;

        var items = wrap.children;
        if (!items.length) return;

        if (typeof index !== 'number' || isNaN(index)) index = 0;
        if (index < 0) index = 0;
        if (index > items.length - 1) index = items.length - 1;

        for (var i = 0; i < items.length; i += 1) {
          if (i === index) items[i].classList.add('is-active');
          else items[i].classList.remove('is-active');
        }
      },
      sync: function (target) {
        if (!target || !target.isConnected) return;
        if (!target.querySelector('.card-gallery__dots')) return;

        var slideshow = target.querySelector('slideshow-component[ref="slideshow"]');
        if (!slideshow) return;

        var index = typeof slideshow.current === 'number' ? slideshow.current : 0;
        dots.setActive(target, index);
      },
      init: function (target) {
        if (!target.querySelector('.card-gallery__dots')) return;

        var slideshow = target.querySelector('slideshow-component[ref="slideshow"]');
        if (!slideshow) return;

        if (slideshow.dataset.jnorssDots === 'true') {
          dots.sync(target);
          return;
        }

        slideshow.dataset.jnorssDots = 'true';

        slideshow.addEventListener('slideshow:select', function (event) {
          var index = event && event.detail ? event.detail.index : null;
          if (typeof index !== 'number') {
            dots.sync(target);
            return;
          }
          dots.setActive(target, index);
        });

        var scroller = slideshow.querySelector('slideshow-slides[ref="scroller"]');
        if (scroller) {
          var frame = null;
          scroller.addEventListener(
            'scroll',
            function () {
              if (frame !== null) return;
              frame = raf(function () {
                frame = null;

                var width = scroller.clientWidth;
                if (!width || scroller.scrollWidth <= width + 1) return;

                dots.setActive(target, Math.round(Math.abs(scroller.scrollLeft) / width));
              });
            },
            { passive: true }
          );
        }

        dots.sync(target);
      }
    };
    window.JnorssCardDots = dots;
  }

  var registry = window.JnorssCardGalleryFix;
  if (!registry) {
    registry = {
      galleries: [],
      installed: false,
      reset: function (target) {
        if (!target || !target.isConnected) return;
        if (!target.closest('product-card')) return;

        var slideshow = target.querySelector('slideshow-component[ref="slideshow"]');
        var scroller = slideshow && slideshow.querySelector('slideshow-slides[ref="scroller"]');
        var slides = [];
        var children = scroller ? scroller.children : [];

        for (var index = 0; index < children.length; index += 1) {
          if (children[index].tagName && children[index].tagName.toLowerCase() === 'slideshow-slide') {
            slides.push(children[index]);
          }
        }

        if (!slideshow || !scroller || !slides.length) return;

        slideshow.setAttribute('initial-slide', '0');
        if ('current' in slideshow) slideshow.current = 0;

        slides.forEach(function (slide, index) {
          slide.removeAttribute('reveal');
          slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
          if (index === 0) slide.hidden = false;
        });

        try {
          scroller.scrollTo({ left: 0, top: 0, behavior: 'auto' });
        } catch (error) {
          scroller.scrollLeft = 0;
          scroller.scrollTop = 0;
        }

        if (window.JnorssCardDots) window.JnorssCardDots.setActive(target, 0);
      },
      resetAll: function () {
        for (var index = this.galleries.length - 1; index >= 0; index -= 1) {
          var target = this.galleries[index];
          if (target.isConnected) this.reset(target);
          else this.galleries.splice(index, 1);
        }
      }
    };
    window.JnorssCardGalleryFix = registry;
  }

  /* --- discovery, viewport gating, idle fallback -------------------------- */

  function settle(gallery) {
    registry.reset(gallery);
    dots.init(gallery);
  }

  function activate(gallery, full) {
    if (gallery.dataset.jnorssGallery === 'active') return;
    gallery.dataset.jnorssGallery = 'active';

    settle(gallery);
    raf(function () {
      settle(gallery);
    });

    // slideshow-component hydrates after its markup is parsed, so a gallery reached during
    // page load needs the later passes the inline version used to do. Galleries swept in
    // idle time are already past that window and do not.
    if (!full) return;
    setTimeout(function () {
      settle(gallery);
    }, 120);
    setTimeout(function () {
      settle(gallery);
    }, 700);
  }

  var observer = null;
  if (typeof window.IntersectionObserver === 'function') {
    observer = new window.IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i += 1) {
          if (!entries[i].isIntersecting) continue;
          observer.unobserve(entries[i].target);
          activate(entries[i].target, true);
        }
      },
      // Settle before the card can be seen, so the reset is never visible to the user.
      { rootMargin: '300px 0px' }
    );
  }

  function register(gallery) {
    if (!gallery.closest('product-card')) return;
    if (registry.galleries.indexOf(gallery) === -1) registry.galleries.push(gallery);

    if (gallery.dataset.jnorssGallery === 'active') return;
    if (observer) observer.observe(gallery);
    else activate(gallery, true);
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var found = scope.querySelectorAll('.card-gallery');
    for (var i = 0; i < found.length; i += 1) register(found[i]);
  }

  // Anything the observer has not reached by load is settled here, a few at a time, so the
  // page is never left depending on IntersectionObserver actually having fired.
  function sweep() {
    var handled = 0;

    for (var i = 0; i < registry.galleries.length && handled < 4; i += 1) {
      var gallery = registry.galleries[i];
      if (!gallery.isConnected) continue;
      if (gallery.dataset.jnorssGallery === 'active') continue;

      if (observer) observer.unobserve(gallery);
      activate(gallery, false);
      handled += 1;
    }

    if (handled) idle(sweep);
  }

  function startSweep() {
    idle(sweep);
  }

  scan(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan(document);
    });
  }

  if (document.readyState === 'complete') startSweep();
  else window.addEventListener('load', startSweep);

  if (!registry.installed) {
    registry.installed = true;

    window.addEventListener('pageshow', function () {
      registry.resetAll();
    });

    document.addEventListener('shopify:section:load', function (event) {
      scan(event.target || document);
      registry.resetAll();
    });

    // Quick add, pagination and section rendering inject cards after load.
    if (typeof window.MutationObserver === 'function') {
      var pending = false;
      new window.MutationObserver(function () {
        if (pending) return;
        pending = true;
        raf(function () {
          pending = false;
          scan(document);
          startSweep();
        });
      }).observe(document.documentElement, { childList: true, subtree: true });
    }
  }
})();
